/**
 * Lighthouse Posture — "are our AI agents under control?"
 */
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  useLighthouseAgents,
  useLighthouseAttestations,
  useLighthouseMcpServers,
} from "@/hooks/security/useLighthouseData";
import { formatDistanceToNow } from "date-fns";

export default function LhPosturePage() {
  return (
    <ConnectorGate connectorId="lighthouse">
      <Inner />
    </ConnectorGate>
  );
}

function Inner() {
  const agentsQ = useLighthouseAgents();
  const attQ = useLighthouseAttestations();
  const mcpQ = useLighthouseMcpServers();

  const agents = agentsQ.data ?? [];
  const sanctioned = agents.filter((a) => a.classification === "sanctioned").length;
  const shadow = agents.filter((a) => a.classification === "shadow").length;
  const unknown = agents.filter((a) => a.classification === "unknown").length;
  const highRisk = agents.filter((a) => a.riskScore >= 70).length;
  const avgRisk = agents.length
    ? Math.round(agents.reduce((a, b) => a + b.riskScore, 0) / agents.length)
    : 0;
  const topRisky = [...agents].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);

  const mcp = mcpQ.data ?? [];
  const unsanctionedMcp = mcp.filter((m) => !m.sanctioned).length;

  return (
    <SurfaceShell
      overline="AI Governance · Posture"
      title="Are our AI agents under control?"
      summary={`${agents.length} agents discovered. ${sanctioned} sanctioned, ${shadow} shadow, ${unknown} unknown. ${highRisk} flagged high-risk.`}
      error={agentsQ.isError ? agentsQ.error : undefined}
      onRetry={agentsQ.isError ? () => agentsQ.refetch() : undefined}
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/ai/approvals">
            Open approvals <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
      kpis={
        <>
          <StatTile label="Agents" value={agents.length} loading={agentsQ.isLoading || agentsQ.isError} hint={`${sanctioned} sanctioned`} />
          <StatTile
            label="High-risk"
            value={highRisk}
            loading={agentsQ.isLoading || agentsQ.isError}
            tone={highRisk > 0 ? "danger" : "success"}
            hint={`avg score ${avgRisk}`}
          />
          <StatTile
            label="Shadow agents"
            value={shadow + unknown}
            loading={agentsQ.isLoading || agentsQ.isError}
            tone={shadow + unknown > 0 ? "warning" : "success"}
            hint="Unsanctioned or unidentified"
          />
          <StatTile
            label="Attestations"
            value={attQ.data?.length ?? 0}
            loading={attQ.isLoading || attQ.isError}
            hint={`${unsanctionedMcp} MCP servers unsanctioned`}
          />
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Top risky agents" actions={
          <Link to="/ai/inventory" className="text-[11px] text-primary hover:underline">View all</Link>
        }>
          {topRisky.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No agents observed yet.</p>
          ) : (
            <ul className="divide-y divide-border -mx-4 -my-4">
              {topRisky.map((a) => (
                <li key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{a.name}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">
                      {a.classification} · {a.vendor}
                    </div>
                  </div>
                  <RiskPill score={a.riskScore} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Classification breakdown">
          <ul className="space-y-2.5">
            {[
              { label: "Sanctioned", value: sanctioned, tone: "bg-success" },
              { label: "Shadow", value: shadow, tone: "bg-warning" },
              { label: "Unknown", value: unknown, tone: "bg-info" },
              { label: "Quarantined", value: agents.filter(a => a.classification === "quarantined").length, tone: "bg-destructive" },
              { label: "Retired", value: agents.filter(a => a.classification === "retired").length, tone: "bg-muted-foreground" },
            ].map((row) => {
              const total = agents.length || 1;
              const pct = (row.value / total) * 100;
              return (
                <li key={row.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground">{row.label}</span>
                    <span className="text-muted-foreground tabular-nums">{row.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${row.tone}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Panel>
      </div>
    </SurfaceShell>
  );
}

function RiskPill({ score }: { score: number }) {
  const tone =
    score >= 70 ? "bg-destructive/15 text-destructive border-destructive/30"
    : score >= 40 ? "bg-warning/15 text-warning-muted-foreground border-warning-border"
    : "bg-success-muted text-success-muted-foreground border-success-border";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold tabular-nums border ${tone}`}>
      {score}
    </span>
  );
}
